import { ConfigProvider, Form, Input, InputNumber, Segmented, Switch } from "antd";
import { useForm } from "antd/es/form/Form";
import PolicySettingsPage, {
  DiffRow,
  fmtBool,
} from "pages/settings/policy/PolicySettingsPage";
import { pillSegmentedTheme } from "pages/analytics/RangeSegmented";
import { SettingRow, SettingsSection } from "pages/settings/settingsRows";
import { translate } from "shared/languages";
import { AdminCapability } from "shared/utils/capabilities";
import { TaxMode } from "shared/types";
import { applyPurchaseTax, decomposeTax } from "shared/utils/tax";
import SettingsChangeLog from "pages/settings/SettingsChangeLog";

type Values = {
  tax_enabled: boolean;
  tax_mode: TaxMode;
  tax_rate?: number;
  tax_label: string;
};

const fmtRate = (rate: number | null) =>
  rate == null ? "-" : `${rate}%`;

const fmtMode = (mode: TaxMode) =>
  translate(`class_settings.tax.mode.${mode}`);

// The four fields this page owns, in the order the form lists them. The
// history tab prints each through the same label and formatter the confirm
// dialog uses, so "Status Pajak: Aktif -> Nonaktif" and "11% -> 12%" read
// the same in both places (the switch itself keeps its command label).
const TAX_FIELDS = ["tax_enabled", "tax_mode", "tax_rate", "tax_label"];
const taxFieldLabel = (field: string) =>
  translate(
    field === "tax_enabled"
      ? "class_settings.tax.status_label"
      : field === "tax_mode"
        ? "class_settings.tax.mode_label"
        : field === "tax_rate"
          ? "class_settings.tax.rate_label"
          : "class_settings.tax.label_label",
  );
const taxFieldValue = (field: string, value: boolean | number | string | null) => {
  if (field === "tax_enabled") return fmtBool(value === true);
  if (field === "tax_mode") return value == null ? "-" : fmtMode(value as TaxMode);
  if (field === "tax_rate") return fmtRate(typeof value === "number" ? value : null);
  return value == null || value === "" ? "-" : String(value);
};

const TaxSettingsPage = () => {
  const [form] = useForm<Values>();

  // Mode/rate/label only show once tax is on. Owned here (not the shell) so
  // these watches are legal.
  const enabled = Form.useWatch("tax_enabled", form);
  const mode = Form.useWatch("tax_mode", form);

  return (
    <PolicySettingsPage<Values>
      // Grouped with catalogue/pricing, not the general operational bucket:
      // tax directly changes what a customer is charged (additive mode),
      // matching the server's POLICY_KEY_CAPABILITY mapping for these fields.
      capability={AdminCapability.CatalogPricing}
      sectioned
      title={translate("class_settings.tax.title")}
      confirmNote={translate("class_settings.confirm.note_tax")}
      historyTab={<SettingsChangeLog fields={TAX_FIELDS} labelOf={taxFieldLabel} format={taxFieldValue} />}
      form={form}
      initialValues={(s) => ({
        tax_enabled: s.tax_enabled,
        // While tax is off the stored mode is not in force, so the form
        // proposes "Ditambahkan ke harga" for the first switch-on (chris,
        // 2026-10-16); a studio with tax on sees its own mode.
        tax_mode: s.tax_enabled ? s.tax_mode : TaxMode.Additive,
        tax_rate: s.tax_rate ?? undefined,
        tax_label: s.tax_label,
      })}
      buildSave={(v, s) => {
        const isEnabled = !!v.tax_enabled;
        const nextRate: number | null = isEnabled ? Number(v.tax_rate) : null;
        const nextMode = v.tax_mode ?? s.tax_mode;
        const nextLabel = v.tax_label ?? s.tax_label;

        const rows: DiffRow[] = [];
        if (isEnabled !== s.tax_enabled) {
          rows.push({
            // The switch is labelled as a command ("Aktifkan ..."); a row
            // that reads Aktif -> Nonaktif names the fact, the way the
            // history tab does.
            label: translate("class_settings.tax.status_label"),
            before: fmtBool(s.tax_enabled),
            after: fmtBool(isEnabled),
          });
        }
        // Switching tax on brings the mode, the rate and the name into force
        // together, so each is listed from "-" (a value stored while tax was
        // off was not in force), the same as the history tab lists them.
        const turningOn = isEnabled && !s.tax_enabled;
        if (isEnabled && (nextMode !== s.tax_mode || turningOn)) {
          rows.push({
            label: translate("class_settings.tax.mode_label"),
            before: s.tax_enabled ? fmtMode(s.tax_mode) : "-",
            after: fmtMode(nextMode),
          });
        }
        if (nextRate !== s.tax_rate) {
          rows.push({
            label: translate("class_settings.tax.rate_label"),
            before: fmtRate(s.tax_rate),
            after: fmtRate(nextRate),
          });
        }
        if (isEnabled && (nextLabel !== s.tax_label || turningOn)) {
          rows.push({
            label: translate("class_settings.tax.label_label"),
            before: s.tax_enabled ? s.tax_label : "-",
            after: nextLabel,
          });
        }

        // Does this change what a client pays, or what the studio keeps
        // after tax? Decided with the same function the server charges
        // with, on the old and the new settings, so the warning appears on
        // exactly the saves that move money: tax on or off, a mode switch,
        // a rate change. A label rename moves nothing and stays silent. The
        // sentence itself stays short by decision (chris, 2026-10-15): the
        // one fact an owner needs is that the catalogue is not adjusted for
        // them, the way Shopify words its own tax-inclusive warning.
        const base = 100_000;
        const next = { tax_enabled: isEnabled, tax_mode: nextMode, tax_rate: nextRate };
        const netOf = (settings: { tax_enabled: boolean; tax_mode: TaxMode; tax_rate: number | null }) =>
          settings.tax_enabled && settings.tax_rate && settings.tax_mode === TaxMode.Inclusive
            ? decomposeTax(base, settings.tax_rate).subtotal
            : base;
        const moneyMoves =
          applyPurchaseTax(base, s) !== applyPurchaseTax(base, next) || netOf(s) !== netOf(next);
        const warnings = moneyMoves ? [translate("class_settings.tax.warn_catalogue")] : [];

        return {
          rows,
          warnings,
          payload: {
            tax_enabled: isEnabled,
            ...(isEnabled
              ? { tax_mode: nextMode, tax_rate: nextRate!, tax_label: nextLabel }
              : {}),
          },
        };
      }}
    >
      <SettingsSection title={translate("class_settings.tax.section_status")}>
        <SettingRow
          last
          title={translate("class_settings.tax.toggle")}
          hint={translate("class_settings.tax.toggle_desc")}
          control={
            <Form.Item name="tax_enabled" valuePropName="checked" noStyle>
              <Switch aria-label={translate("class_settings.tax.toggle")} />
            </Form.Item>
          }
        />
      </SettingsSection>

      {/* Mode, rate and name unmount while tax is off; AntD keeps their
          values in the form store, and buildSave neither lists nor sends them. */}
      {enabled && (
        <SettingsSection title={translate("class_settings.tax.section_details")}>
          <SettingRow
            title={translate("class_settings.tax.mode_label")}
            hint={mode ? translate(`class_settings.tax.mode_desc.${mode}`) : undefined}
            control={
              // ConfigProvider must wrap the Form.Item, not sit inside it:
              // Form.Item clones its direct child to inject value/onChange,
              // and ConfigProvider (a plain context provider) drops both, the
              // gotcha DiscountCodesCard's mode Segmented ran into.
              <ConfigProvider theme={pillSegmentedTheme}>
                <Form.Item
                  name="tax_mode"
                  style={{ margin: 0 }}
                  rules={[{ required: true, message: translate("form.error.required") }]}
                >
                  <Segmented
                    aria-label={translate("class_settings.tax.mode_label")}
                    options={[
                      {
                        value: TaxMode.Inclusive,
                        label: translate("class_settings.tax.mode.inclusive"),
                      },
                      {
                        value: TaxMode.Additive,
                        label: translate("class_settings.tax.mode.additive"),
                      },
                    ]}
                  />
                </Form.Item>
              </ConfigProvider>
            }
          />
          <SettingRow
            title={translate("class_settings.tax.rate_label")}
            control={
              <Form.Item
                name="tax_rate"
                style={{ margin: 0 }}
                rules={[
                  {
                    required: true,
                    type: "number",
                    min: 0.01,
                    max: 100,
                    message: translate("class_settings.tax.rate_error"),
                  },
                ]}
              >
                <InputNumber
                  aria-label={translate("class_settings.tax.rate_label")}
                  min={0.01}
                  max={100}
                  precision={2}
                  step={0.01}
                  style={{ width: 160 }}
                  addonAfter="%"
                />
              </Form.Item>
            }
          />
          <SettingRow
            last
            title={translate("class_settings.tax.label_label")}
            control={
              <Form.Item
                name="tax_label"
                style={{ margin: 0 }}
                rules={[{ required: true, message: translate("form.error.required") }]}
              >
                <Input
                  aria-label={translate("class_settings.tax.label_label")}
                  maxLength={50}
                  style={{ width: 240 }}
                  placeholder={translate("class_settings.tax.label_placeholder")}
                />
              </Form.Item>
            }
          />
        </SettingsSection>
      )}
    </PolicySettingsPage>
  );
};

export default TaxSettingsPage;
